import { Fragment, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Info, UserRound } from 'lucide-react';
import { useWhatsappMessages, useWhatsappContact, useWhatsappWebMutations, whatsappError } from '../whatsappWebApi';
import { Spinner } from '@/shared/components/ui/Spinner';
import { MessageBubble } from './MessageBubble';
import { MessageComposer } from './MessageComposer';

interface ChatWindowProps {
  chatId: string;
  /** Business API inbox (templates, reply window) vs the linked-phone session. */
  businessApi: boolean;
  /** Phones: back to the conversation list. */
  onBack: () => void;
}

function dayLabel(ts: number) {
  const d = new Date(ts * 1000);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function ChatWindow({ chatId, businessApi, onBack }: ChatWindowProps) {
  const { data, isLoading, error } = useWhatsappMessages(chatId);
  const { data: contact } = useWhatsappContact(chatId);
  const { markRead } = useWhatsappWebMutations();
  const messages = data?.messages ?? [];
  const replyWindow = data?.window;
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages.length, chatId]);

  // Opening a conversation (and every new customer message in it) marks it read.
  const lastInbound = [...messages].reverse().find((m) => !m.fromMe)?.id;
  useEffect(() => {
    if (businessApi && chatId && !error) markRead.mutate(chatId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, lastInbound, businessApi]);

  const phone = contact?.number || (chatId.length === 10 ? `+91 ${chatId.slice(0, 5)} ${chatId.slice(5)}` : chatId);
  const displayName = contact?.name || phone || 'WhatsApp chat';
  const first = displayName.replace(/^\+/, '').charAt(0).toUpperCase();
  const initial = /\d/.test(first) ? '#' : first || 'C';
  const windowOpen = !businessApi || Boolean(replyWindow?.open);
  const closesAt = replyWindow?.closesAt ? new Date(replyWindow.closesAt) : null;

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden bg-[#efeae2]">
      <div className="z-10 flex h-[59px] shrink-0 items-center justify-between gap-2 bg-[#f0f2f5] px-2 shadow-sm md:px-4">
        <div className="flex min-w-0 items-center gap-2 md:gap-3">
          <button type="button" onClick={onBack} className="rounded-full p-2 text-[#54656f] hover:bg-black/5 md:hidden" aria-label="Back to chats">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#dfe5e7] text-[#54656f]">
            {contact?.picUrl ? <img src={contact.picUrl} alt={displayName} className="h-full w-full object-cover" /> : <span className="text-body-lg font-medium">{initial}</span>}
          </div>
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-[16px] font-medium leading-tight text-[#111b21]">{displayName}</span>
            <span className="truncate text-[12.5px] leading-tight text-[#667781]">
              {displayName !== phone ? phone : 'WhatsApp'}
              {businessApi && windowOpen && closesAt && ` · reply window open till ${closesAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
            </span>
          </div>
        </div>
        {contact?.contactId && (
          <Link to={`/contacts/${contact.contactId}`} className="flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-[13px] text-[#008069] hover:bg-black/5" title="Open in Contacts">
            <UserRound className="h-4 w-4" /> <span className="hidden sm:inline">Contact</span>
          </Link>
        )}
      </div>

      <div className="relative z-10 flex flex-1 flex-col overflow-y-auto px-3 py-4 md:px-[6%]">
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <div className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[14px] text-[#54656f] shadow-sm">
              <Spinner className="h-4 w-4 text-[#00a884]" /> Loading messages…
            </div>
          </div>
        ) : error ? (
          <div className="flex flex-1 items-center justify-center">
            <p className="max-w-sm rounded-lg bg-white px-4 py-3 text-center text-[14px] text-[#54656f] shadow-sm">{whatsappError(error, 'Could not open this conversation.')}</p>
          </div>
        ) : (
          <div className="flex flex-1 flex-col">
            <div className="mx-auto mb-4 mt-1 max-w-md self-center rounded-[8px] bg-[#ffeecd] px-3 py-1.5 text-center text-[12.5px] text-[#54656f] shadow-[0_1px_0.5px_rgba(11,20,26,.13)]">
              <Info className="mr-1.5 inline-block h-3 w-3 align-middle" aria-hidden />
              Messages here are saved in ZYNX for your team's records.
            </div>
            {messages.length === 0 && (
              <p className="mt-6 self-center text-center text-[13px] text-[#667781]">No messages yet. {businessApi ? 'Start the conversation with an approved template.' : ''}</p>
            )}
            {messages.map((msg, i) => {
              const prev = messages[i - 1];
              const showDay = msg.timestamp && (!prev?.timestamp || dayLabel(prev.timestamp) !== dayLabel(msg.timestamp));
              return (
                <Fragment key={msg.id || i}>
                  {showDay && (
                    <div className="my-2 self-center rounded-[7.5px] bg-white px-3 py-1 text-[12px] uppercase text-[#54656f] shadow-[0_1px_0.5px_rgba(11,20,26,.13)]">
                      {dayLabel(msg.timestamp!)}
                    </div>
                  )}
                  <MessageBubble msg={msg} />
                </Fragment>
              );
            })}
            <div ref={bottomRef} className="h-1 shrink-0" />
          </div>
        )}
      </div>

      {!error && (
        <div className="relative z-10">
          <MessageComposer chatId={chatId} businessApi={businessApi} windowOpen={windowOpen} contactName={contact?.name} />
        </div>
      )}
    </div>
  );
}
